import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import {
  ScholarshipManagementDto,
  StateDto,
  LgaDto,
  ScholarshipTypeDto,
  ScholarshipCategoryDto,
  ScholarshipSessionDto,
  ScholarshipAwardDurationDto,
  CommunityDto,
  CourseOfStudyDto,
  EpimsGenderDto,
  CountryDto,
} from '@cradua/data';
import { MatStepper } from '@angular/material/stepper';
import { TooltipPosition } from '@angular/material/tooltip';
import {
  FormControl,
  FormGroup,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'mrsoftapps-create-scholarship-management',
  templateUrl: './create-scholarship-management.component.html',
  styleUrls: ['./create-scholarship-management.component.scss'],
})
export class CreateScholarshipManagementComponent implements OnInit {
  /** control for the selected item for option groups */
  countryGroupsCtrl: FormControl = new FormControl();
  scholarshipCategoryGroupsCtrl: FormControl = new FormControl();

  /** control for the MatSelect filter keyword for option groups */
  countryGroupsFilterCtrl: FormControl = new FormControl();
  scholarshipCategoryGroupsFilterCtrl: FormControl = new FormControl();

  /** list of item groups filtered by search keyword for option groups */
  filteredStateGroups: ReplaySubject<StateDto[]> = new ReplaySubject<
    StateDto[]
  >(1);
  filteredScholarshipTypeGroups: ReplaySubject<
    ScholarshipCategoryDto[]
  > = new ReplaySubject<ScholarshipCategoryDto[]>(1);

  /** Subject that emits when the component has been destroyed. */
  protected _onDestroy = new Subject<void>();

  @Input() countries: CountryDto[];
  @Input() states: StateDto[];
  @Input() lgas: LgaDto[];
  @Input() scholarshipTypes: ScholarshipTypeDto[];
  @Input() scholarshipCategories: ScholarshipCategoryDto[];
  @Input() scholarshipData: ScholarshipManagementDto;
  @Input() courseOfStudies: CourseOfStudyDto[];
  @Input() scholarshipSessions: ScholarshipSessionDto[];
  @Input() scholarshipAwardDurations: ScholarshipAwardDurationDto[];
  @Input() genders: EpimsGenderDto[];
  @Input() communities: CommunityDto[];
  @Input() scholarshipManagements: ScholarshipManagementDto[];
  @Output() changeEmit = new EventEmitter<any>();
  @Output() taskEmit = new EventEmitter<any>();

  scholarshipManagementForm: FormGroup;
  @ViewChild('stepper', { static: false })
  private myStepper: MatStepper;
  totalStepsCount: number;
  stepperIndex: number;
  filteredCommunityOnAward = [];
  filteredCommunityBeforeAward = [];
  filteredCommunityBeneficiary = [];
  filteredScholarshipType = [];
  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    this.scholarshipManagementFormValidation();

    // load the initial bank list
    this.filteredStateGroups.next(this.copyStateGroups(this.states));
    this.filteredScholarshipTypeGroups.next(
      this.copyScholarshipCategoryGroups(this.scholarshipCategories)
    );

    // listen for search field value changes
    this.countryGroupsFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterStateGroups();
      });

    this.scholarshipCategoryGroupsFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterScholarshipTypeGroups();
      });

    if (this.scholarshipData) {
      this.getEditFilterData(this.scholarshipData);
      this.scholarshipManagementForm.patchValue(this.scholarshipData);
    }
  }

  getEditFilterData(value: ScholarshipManagementDto) {
    this.filteredCommunityBeneficiary = this.communities.filter(
      (item) => item.LgaId === value.LGA
    );
    this.filteredCommunityBeforeAward = this.communities.filter(
      (s) => s.LgaId === value.InstitutionLGABeforeAward
    );
    this.filteredCommunityOnAward = this.communities.filter(
      (s) => s.LgaId === value.InstitutionLGAOnAward
    );
  }

  scholarshipManagementFormValidation() {
    this.scholarshipManagementForm = this.fb.group({
      ScholarshipCategoryId: ['', Validators.required],
      ScholarshipTypeId: ['', Validators.required],
      BeneficarySurname: ['', Validators.required],
      BeneficaryFirstname: ['', Validators.required],
      BeneficaryOthername: [''],
      ScholarshipAwardNo: [''],
      ScholarshipApplicationNo: [''],
      ScholarshipYear: [''],
      ScholarshipDate: ['', Validators.required],
      Gender: ['', Validators.required],
      Address: [''],
      DateOfBirth: [''],
      School: [''],
      PhoneNumber: [''],
      CourseOfStudy: [''],
      ScholarshipAwardDurationId: [''],
      State: [''],
      LGA: [''],
      Community: [''],
      ScholarshipStatus: [''],
      StudentPassport: [''],
      InstitutionCountryIdBeforeAward: [''],
      InstitutionStateIdBeforeAward: [''],
      InstitutionLGABeforeAward: [''],
      InstitutionCommunityBeforeAward: [''],
      ContactPerson: [''],
      ContactPhoneNo: [''],
      ContactEmailOnAward: [''],
      ContactPersonOnAward: [''],
      ContactPhoneNoOnAward: [''],
      ContactUrlOnAward: [''],
      RemarksBeforeAward: [''],
      InstitutionNameBeforeAward: [''],
      InstitutionAddressBeforeAward: [''],
      InstitutionNameOnAward: [''],
      InstitutionAddressOnAward: [''],
      InstitutionCountryIdOnAward: [''],
      InstitutionStateIdOnAward: [''],
      InstitutionLGAOnAward: [''],
      InstitutionCommunityOnAward: [''],
      RemarksOnAward: [''],
      ContactEmail: ['', [Validators.required, Validators.email]],
      id: [''],
    });
  }

  /* Handle form errors in Angular 8 */
  public errorHandling = (control: string, error: string) => {
    return this.scholarshipManagementForm.controls[control].hasError(error);
  };

  onBack() {
    this.taskEmit.emit('list');
  }

  goBack(stepper: MatStepper) {
    stepper.previous();
  }

  goForward(stepper: MatStepper) {
    // this.myStepper.selectedIndex = 1;

    stepper.next();
    this.totalStepsCount = this.myStepper._steps.length;
  }

  selectLgaOnAward(event) {
    this.filteredCommunityOnAward = this.communities.filter(
      (s) => s.LgaId === event.value
    );
  }

  selectLgaBeforeAward(event) {
    this.filteredCommunityBeforeAward = this.communities.filter(
      (s) => s.LgaId === event.value
    );
  }

  selectLgaBeneficiary(event) {
    this.filteredCommunityBeneficiary = this.communities.filter(
      (s) => s.LgaId === event.value
    );
  }

  onImagePicked(event: Event) {
    const file = (event.target as HTMLInputElement).files[0];
    this.scholarshipManagementForm.patchValue({ StudentPassport: file });
    this.scholarshipManagementForm
      .get('StudentPassport')
      .updateValueAndValidity();
    const reader = new FileReader();
    reader.onload = () => {
      // this.imagePreview = reader.result;
      // console.log('image ', this.imagePreview);
    };
    reader.readAsDataURL(file);
  }

  getState(id) {
    const selectedLga = this.lgas.find((item) => item.id === id);

    if (!selectedLga) {
      return;
    }

    const selectedState = this.states.find(
      (item) => item.id === selectedLga.StateId
    );

    if (!selectedState) {
      return;
    }

    return selectedState;
  }

  getScholarshipCategory(id) {
    const selectedType = this.scholarshipTypes.find((item) => item.id === id);
    if (!selectedType) {
      return;
    }

    const selectedCategory = this.scholarshipCategories.find(
      (item) => item.id === selectedType.ScholarshipCategoryId
    );

    if (!selectedCategory) {
      return;
    }

    return selectedCategory;
  }

  onSubmit(value) {
    const selectedLGA = this.getState(value.LGA);
    const selectedLGABefore = this.getState(value.InstitutionLGABeforeAward);
    const selectedLGAOnAWard = this.getState(value.InstitutionLGAOnAward);
    const selectedScholarshipType = this.getScholarshipCategory(
      value.ScholarshipTypeId
    );
    value.ScholarshipStatus = 'Pending';

    if (selectedScholarshipType) {
      value.ScholarshipCategoryId = selectedScholarshipType.id;
    }
    if (selectedLGA) {
      value.State = selectedLGA.id;
    }

    if (selectedLGABefore) {
      value.InstitutionStateIdBeforeAward = selectedLGABefore.id;
      value.InstitutionCountryIdBeforeAward = selectedLGABefore.CountryId;
    }

    if (value.DateOfBirth === '') {
      value.DateOfBirth = new Date();
    }

    if (value.ScholarshipDate === '') {
      value.ScholarshipDate = new Date();
    }

    if (selectedLGAOnAWard) {
      value.InstitutionStateIdOnAward = selectedLGAOnAWard.id;
      value.InstitutionCountryIdOnAward = selectedLGAOnAWard.CountryId;
    }
    // console.log('data from ui ', value.ScholarshipCategoryId);
    this.changeEmit.emit(value);
    this.taskEmit.emit('list');
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  protected filterStateGroups() {
    if (!this.states) {
      return;
    }
    // get the search keyword
    let search = this.countryGroupsFilterCtrl.value;
    const countryGroupsCopy = this.copyStateGroups(this.states);
    if (!search) {
      this.filteredStateGroups.next(countryGroupsCopy);
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the banks
    this.filteredStateGroups.next(
      countryGroupsCopy.filter((state) => {
        const showStateGroup =
          state.StateName.toLowerCase().indexOf(search) > -1;
        if (!showStateGroup) {
          state.lgas = state.lgas.filter(
            (lga) => lga.LgaName.toLowerCase().indexOf(search) > -1
          );
        }
        return state.lgas.length > 0;
      })
    );
  }

  protected filterScholarshipTypeGroups() {
    if (!this.scholarshipTypes) {
      return;
    }
    // get the search keyword
    let search = this.scholarshipCategoryGroupsFilterCtrl.value;
    const scholarshipCategoryGroupsCopy = this.copyScholarshipCategoryGroups(
      this.scholarshipCategories
    );
    if (!search) {
      this.filteredScholarshipTypeGroups.next(scholarshipCategoryGroupsCopy);
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the banks
    this.filteredScholarshipTypeGroups.next(
      scholarshipCategoryGroupsCopy.filter((scholarshipCategory) => {
        const showScholarshipCategoryGroup =
          scholarshipCategory.ScholarshipCategoryName.toLowerCase().indexOf(
            search
          ) > -1;
        if (!showScholarshipCategoryGroup) {
          scholarshipCategory.scholarshipTypes = scholarshipCategory.scholarshipTypes.filter(
            (scholarshipType) =>
              scholarshipType.ScholarshipTypeName.toLowerCase().indexOf(
                search
              ) > -1
          );
        }
        return scholarshipCategory.scholarshipTypes.length > 0;
      })
    );
  }

  protected copyStateGroups(stateGroups: StateDto[]) {
    const countryGroupsCopy = [];
    stateGroups.forEach((stateGroup) => {
      countryGroupsCopy.push({
        StateName: stateGroup.StateName,
        lgas: stateGroup.lgas.slice(),
      });
    });
    // console.log('this is the value got ', countryGroupsCopy);
    return countryGroupsCopy;
  }

  protected copyScholarshipCategoryGroups(
    scholarshipCategoryGroups: ScholarshipCategoryDto[]
  ) {
    const scholarshipCategoryGroupsCopy = [];
    scholarshipCategoryGroups.forEach((scholarshipCategoryGroup) => {
      scholarshipCategoryGroupsCopy.push({
        ScholarshipCategoryName:
          scholarshipCategoryGroup.ScholarshipCategoryName,
        scholarshipTypes: scholarshipCategoryGroup.scholarshipTypes.slice(),
      });
    });
    // console.log('this is the value got ', countryGroupsCopy);
    return scholarshipCategoryGroupsCopy;
  }
}
